import { Checkbox, CheckboxField, Label, Option, Select, Textarea, TextInput } from '@d-edge/overnight-hotelier-react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { format } from 'date-fns'
import { useJobMutations } from '@/api/queries'
import type { Frequency, ScheduledJob, ScheduledJobInput } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { toDate } from '@/lib/format'
import styles from './scheduler.module.css'

const FREQUENCIES: Frequency[] = ['Minutes', 'Hours', 'Days', 'Weeks', 'Months']
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
const AUTHORIZATIONS = ['None', 'Basic']
const LOCAL_FORMAT = "yyyy-MM-dd'T'HH:mm"
const TIME_PATTERN = /^\d{2}:\d{2}:\d{2}$/
const NATIVE_INPUT = 'htl-text-input htl-text-input--full-width'

interface FormState {
  schedulerName: string
  name: string
  description: string
  enabled: boolean
  interval: string
  frequency: Frequency
  start: string
  end: string
  url: string
  httpMethod: string
  authorization: string
  useRecommendedEncryptionMethod: boolean
  timeout: string
  body: string
  contentType: string
  /** Secrets are write-only: the API never returns them, so edits must opt in to replace them. */
  replaceSecrets: boolean
  headers: string
  login: string
  password: string
}

function initialState(job: ScheduledJob | null): FormState {
  if (!job) {
    return {
      schedulerName: '', name: '', description: '', enabled: true, interval: '1', frequency: 'Hours',
      start: format(new Date(), LOCAL_FORMAT), end: '', url: '', httpMethod: 'GET', authorization: 'None',
      useRecommendedEncryptionMethod: true, timeout: '', body: '', contentType: '',
      replaceSecrets: true, headers: '', login: '', password: '',
    }
  }
  return {
    schedulerName: job.schedulerName, name: job.name, description: job.description ?? '', enabled: job.enabled,
    interval: String(job.interval), frequency: job.frequency, start: format(toDate(job.start), LOCAL_FORMAT),
    end: job.end ? format(toDate(job.end), LOCAL_FORMAT) : '', url: job.url, httpMethod: job.httpMethod,
    authorization: job.authorization, useRecommendedEncryptionMethod: job.useRecommendedEncryptionMethod,
    timeout: job.timeout ?? '', body: job.body ?? '', contentType: job.contentType ?? '',
    replaceSecrets: false, headers: '', login: '', password: '',
  }
}

function validate(s: FormState): string | null {
  if (!s.schedulerName.trim() || !s.name.trim()) return 'Scheduler and job names are required.'
  if (!/^https?:\/\//i.test(s.url)) return 'The URL must start with http:// or https://.'
  if (!(Number(s.interval) >= 1)) return 'The interval must be at least 1.'
  if (s.timeout && !TIME_PATTERN.test(s.timeout)) return 'The timeout must look like 00:05:00.'
  if (s.end && new Date(s.end) <= new Date(s.start)) return 'The end date must be after the start date.'
  return null
}

function toInput(s: FormState): ScheduledJobInput {
  return {
    schedulerName: s.schedulerName.trim(),
    name: s.name.trim(),
    description: s.description || null,
    enabled: s.enabled,
    interval: Number(s.interval),
    frequency: s.frequency,
    start: new Date(s.start).toISOString(),
    end: s.end ? new Date(s.end).toISOString() : null,
    url: s.url.trim(),
    httpMethod: s.httpMethod,
    authorization: s.authorization,
    useRecommendedEncryptionMethod: s.useRecommendedEncryptionMethod,
    timeout: s.timeout || null,
    body: s.body || null,
    contentType: s.contentType || null,
    headers: s.replaceSecrets ? s.headers : null,
    login: s.replaceSecrets ? s.login : null,
    password: s.replaceSecrets ? s.password : null,
  }
}

function Check({ checked, onChange, children }: { checked: boolean; onChange: (checked: boolean) => void; children: ReactNode }) {
  return (
    <CheckboxField>
      <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <Label>{children}</Label>
    </CheckboxField>
  )
}

export function JobForm({ job, onClose }: { job: ScheduledJob | null; onClose: () => void }) {
  const [s, setState] = useState(() => initialState(job))
  const [error, setError] = useState<string | null>(null)
  const { create, update } = useJobMutations()
  const toast = useToast()
  const busy = create.isPending || update.isPending
  const set = (patch: Partial<FormState>) => setState((current) => ({ ...current, ...patch }))

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const problem = validate(s)
    setError(problem)
    if (problem) return
    try {
      if (job) await update.mutateAsync({ id: job.id, input: toInput(s) })
      else await create.mutateAsync(toInput(s))
      toast.notify(`${s.name} saved`, 'success')
      onClose()
    } catch (err) {
      toast.error(err, 'Save failed')
    }
  }

  const text = (key: keyof FormState, label: string, extra: { required?: boolean; placeholder?: string; mono?: boolean } = {}) => (
    <Field label={label} required={extra.required}>
      <TextInput fullWidth value={String(s[key])} placeholder={extra.placeholder} required={extra.required} className={extra.mono ? 'u-mono' : undefined} onChange={(e) => set({ [key]: e.target.value })} />
    </Field>
  )

  return (
    <Modal
      title={job ? `Edit ${job.name}` : 'New scheduled job'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" type="submit" form="job-form" loading={busy}>Save</Button>
        </>
      }
    >
      <form id="job-form" className={styles.form} onSubmit={submit}>
        {text('schedulerName', 'Scheduler', { required: true, placeholder: 'e.g. RateScreener' })}
        {text('name', 'Job name', { required: true })}
        <div className={styles.span2}>{text('description', 'Description')}</div>
        <Field label="Every" htmlFor="job-interval">
          <div className="u-row">
            <input id="job-interval" type="number" min={1} value={s.interval} onChange={(e) => set({ interval: e.target.value })} className="htl-text-input" style={{ width: 90 }} />
            <Select value={s.frequency} onChange={(value) => set({ frequency: value as Frequency })} aria-label="Frequency unit" className="u-grow">
              {FREQUENCIES.map((f) => <Option key={f} value={f} label={f.toLowerCase()} />)}
            </Select>
          </div>
        </Field>
        <div style={{ alignSelf: 'end' }}>
          <Check checked={s.enabled} onChange={(enabled) => set({ enabled })}>Enabled</Check>
        </div>
        <Field label="Start" htmlFor="job-start" required>
          <input id="job-start" type="datetime-local" value={s.start} onChange={(e) => set({ start: e.target.value })} required className={NATIVE_INPUT} />
        </Field>
        <Field label="End" htmlFor="job-end" hint="Leave empty for no end">
          <input id="job-end" type="datetime-local" value={s.end} onChange={(e) => set({ end: e.target.value })} className={NATIVE_INPUT} />
        </Field>
        <div className={styles.span2}>{text('url', 'URL', { required: true, placeholder: 'https://' })}</div>
        <Field label="Method">
          <Select fullWidth value={s.httpMethod} onChange={(httpMethod) => set({ httpMethod })}>
            {METHODS.map((m) => <Option key={m} value={m} label={m} />)}
          </Select>
        </Field>
        <Field label="Authorization">
          <Select fullWidth value={s.authorization} onChange={(authorization) => set({ authorization })}>
            {AUTHORIZATIONS.map((a) => <Option key={a} value={a} label={a} />)}
          </Select>
        </Field>
        {text('timeout', 'Timeout', { placeholder: '00:05:00' })}
        {text('contentType', 'Content type', { placeholder: 'application/json' })}
        <div className={styles.span2}>
          <Field label="Body">
            <Textarea rows={4} fullWidth resize="vertical" value={s.body} onChange={(e) => set({ body: e.target.value })} className="u-mono" />
          </Field>
        </div>
        <div className={`${styles.span2} ${styles.secrets}`}>
          {job && (
            <Check checked={s.replaceSecrets} onChange={(replaceSecrets) => set({ replaceSecrets })}>
              Replace the stored headers and credentials{job.hasHeaders || job.hasCredentials ? ' (some are stored)' : ''}
            </Check>
          )}
          {s.replaceSecrets && (
            <>
              <Field label="Headers" hint={'Format: {"Name","Value"},{"Name2","Value2"}'}>
                <TextInput fullWidth value={s.headers} onChange={(e) => set({ headers: e.target.value })} className="u-mono" />
              </Field>
              {s.authorization === 'Basic' && (
                <div className="u-row">
                  <Field label="Login" className="u-grow">
                    <TextInput fullWidth value={s.login} autoComplete="off" onChange={(e) => set({ login: e.target.value })} />
                  </Field>
                  <Field label="Password" className="u-grow">
                    <TextInput fullWidth type="password" value={s.password} autoComplete="new-password" onChange={(e) => set({ password: e.target.value })} />
                  </Field>
                </div>
              )}
              <Check checked={s.useRecommendedEncryptionMethod} onChange={(useRecommendedEncryptionMethod) => set({ useRecommendedEncryptionMethod })}>
                Use the recommended encryption for secrets
              </Check>
            </>
          )}
        </div>
        {error && <div className={`${styles.span2} u-danger`}>{error}</div>}
      </form>
    </Modal>
  )
}
